import React, { FC } from 'react'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { useDispatch } from 'react-redux'
import { Form, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { ReminderSettingsSubmitEvent } from '../../Containers/ReminderSettingsContainer'
import TextFieldWrapper from '../Form/TextField'
import {PrimaryButton} from '../Button'
import {registerPlateFormatter} from '../../Utils/helpers'
import {a11yClick} from '../../Utils/a11yHelpers'
import ReminderActions from '../../Redux/reminderRedux'
import {ReminderSettings} from '../../interfaces'

interface ReminderSettingsFormProps {
    fetching: boolean
    prefill: ReminderSettings
    showRemove?: boolean
}

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadows[4],
        padding: theme.spacing(3),
        flex: 1,
        borderRadius: 8,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
        },
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        marginTop: 0,
    },
    formField: {
        '&:nth-of-type(1)': {
            marginTop: 0,
        },
        marginTop: theme.spacing(1),
        width: 250,
        [theme.breakpoints.down('sm')]: {
            width: 200,
        },
    },
    submitButton: {
        borderRadius: 24,
        marginTop: theme.spacing(3),
        width: 250,
    },
    textField: {
        width: 250,
        paddingRight: '0 !important',
        [theme.breakpoints.down('sm')]: {
            width: 200,
        },
    },
    buttonText: {
        fontWeight: 'bold',
        color: theme.palette.grey['100'],
    },
    datePicker: {
        margin: 0,
        width: 250,
        [theme.breakpoints.down('sm')]: {
            width: 200,
        },
    },
    contentEnd: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        flex: 1,
        alignSelf: 'stretch',
    },
}))

export const ReminderSettingsForm: FC<ReminderSettingsFormProps> = ({
    fetching,
    prefill,
    showRemove = true,
}) => {
    const { t } = useTranslation()
    const { classes } = useStyles()
    const classesProps = {
        classes: { root: classes.textField },
    }

    const dispatch = useDispatch()

    const onSubmitReminderUpdate = (e: ReminderSettingsSubmitEvent) => {
        console.log('submit reminder', e)
        dispatch(ReminderActions.postReminderSettings(e))
    }

    const validateReminderSettingsForm = (_values: any): object => {
        return {}
    }

    return (
        <Form
            onSubmit={(e: ReminderSettingsSubmitEvent) => onSubmitReminderUpdate(e)}
            validate={validateReminderSettingsForm}
            initialValues={prefill ? {...prefill, registrationNumber: registerPlateFormatter(prefill.registrationNumber)} : {}}
        >
            {({values, handleSubmit, hasValidationErrors}) => (
                <>
                    <div className={classes.container}>
                        <Field
                            name="registrationNumber"
                            type="text"
                            format={registerPlateFormatter}
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    variant="standard"
                                    label={t('common.registrationNumber')}
                                    placeholder={t('common.registrationNumber')}
                                />
                            )}
                        </Field>
                        {showRemove && <Field
                            name="oldRegistrationNumber"
                            type="text"
                            format={registerPlateFormatter}
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    variant="standard"
                                    label={'Poista ' + t('common.registrationNumber')}
                                    placeholder={'Poista ' + t('common.registrationNumber')}
                                />
                            )}
                        </Field>}
                        <Field
                            name="firstName"
                            type="text"
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    aria-label="firstName"
                                    variant="standard"
                                    label={t('common.firstName')}
                                    placeholder={t('common.firstName')}
                                />
                            )}
                        </Field>
                        <Field
                            name="lastName"
                            type="text"
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    aria-label="lastName"
                                    variant="standard"
                                    label={t('common.lastName')}
                                    placeholder={t('common.lastName')}
                                />
                            )}
                        </Field>
                        <Field
                            name="email"
                            type="text"
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    aria-label="email"
                                    variant="standard"
                                    label={t('common.email')}
                                    placeholder={t('common.email')}
                                />
                            )}
                        </Field>
                        <Field
                            name="phone"
                            type="text"
                            className={classes.formField}
                        >
                            {(props) => (
                                <TextFieldWrapper
                                    {...props}
                                    {...classesProps}
                                    aria-label="phone"
                                    variant="standard"
                                    label={t('common.phone')}
                                    placeholder={t('common.phone')}
                                    type="tel"
                                />
                            )}
                        </Field>
                    </div>
                    <div className={classes.contentEnd}>
                        <PrimaryButton
                            id="reminder-settings-submit"
                            className={classes.submitButton}
                            {...a11yClick(() => {
                                handleSubmit()
                            })}
                            disabled={
                                !(
                                    !hasValidationErrors &&
                                    (values.registrationNumber || values.oldRegistrationNumber) &&
                                    (values.email || values.phone)
                                )
                            }
                            fetching={fetching}
                            type="submit"
                        >
                            {t('reminderSettings.submit')}
                        </PrimaryButton>
                    </div>
                </>
            )}
        </Form>
    )
}
